<template>
  <div class="p-fluid list-form">
    <div class="field" :class="{ 'field-error': hasError('id_list') }">
      <EditAutocomplete
        v-model="selectedLists"
        field="name"
        title="automatedScenarios.actions.list.title"
        :complete="(query) => fetchLists(query)"
        :add-to-list="addToList"
      />
      <FieldErrors
        :key="componentFieldErrorsKey"
        :errors="formValidation"
        field="id_list"
      />
    </div>
    <div class="field-wrapper">
      <div class="field" :class="{ 'field-error': hasError('multiplebox_type') }">
        <ButtonGroup
          v-model="type"
          :options="options"
          title="automatedScenarios.actions.listActionChoice"
        />
        <FieldErrors
          :key="componentFieldErrorsKey"
          :errors="formValidation"
          field="multiplebox_type"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  PropType,
  ref,
  watch,
  computed,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/store';
import type { ShopsListsMin } from '@/types/flow-types';
import { ShopsListsList } from '@/composables/shop/ShopsLists';
import { UserState } from '@/composables/User';
import { asInt } from '@/helpers';
import { hasFieldError } from '@/helpers/formValidation';
import FieldErrors from '@/components/fields/partials/FieldErrors.vue';
import { ButtonGroup, EditAutocomplete } from '@/components/flows/operators/shared';
import type { ListData } from './index';

export default defineComponent({
  name: 'ListForm',

  components: {
    FieldErrors,
    EditAutocomplete,
    ButtonGroup,
  },

  props: {
    modelValue: {
      type: Object as PropType<ListData>,
      required: true,
    },
    formValidation: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const store = useStore();
    const componentFieldErrorsKey = ref(0);

    watch(() => props.formValidation, () => {
      componentFieldErrorsKey.value += 1;
    });

    const selectedLists: any = ref(
      props.modelValue.id_list && props.modelValue.id_list.length > 0 && Object.keys(props.modelValue.id_list[0]).includes('id_shop_list')
        ? props.modelValue.id_list
        : Object.entries(props.modelValue.id_list).map(([id, name]) => ({
            id_shop_list: parseInt(id, 10),
            name,
          }))
    );

    const idShop = ref(UserState.activeShop ? UserState.activeShop.id : 0);
    // Ensure multiplebox_type is a number (may come as string from DB)
    const type = ref(typeof props.modelValue.multiplebox_type === 'string' 
      ? parseInt(props.modelValue.multiplebox_type, 10) 
      : props.modelValue.multiplebox_type ?? 0);

    const options = [
      { value: 0, label: t('automatedScenarios.actions.add') },
      { value: 1, label: t('automatedScenarios.actions.remove') },
    ];

    async function fetchLists(listFilter: string) {
      const result: ShopsListsMin[] | null = await ShopsListsList({ shopId: idShop.value, name: listFilter });
      if (result != null && result.length > 0) {
        return result;
      }
      return [{ id_shop_list: 0, id_shop: idShop.value, name: listFilter }];
    }

    const addToList = async (list: any) => {
      const newList: any[] = [];

      for (let i = 0; i < list.length; i++) {
        const item = list[i];

        if (item.id_shop_list === 0) {
          const newItem = await store.dispatch('listImportEditor/saveNoImportAction', {
            idShop: idShop.value,
            listName: item.name,
            returnResult: true,
          });
          if (newItem) {
            item.id_shop_list = asInt(newItem.id);
          }
        }

        newList.push(item);
      }

      return newList;
    };

    watch([selectedLists, type], () => {
      emit('update:modelValue', {
        ...props.modelValue,
        id_list: selectedLists.value,
        multiplebox_type: type.value,
      });
    });

    const hasError = (fieldName: string) => {
      return hasFieldError(props.formValidation, fieldName);
    };

    return {
      componentFieldErrorsKey,
      type,
      options,
      selectedLists,
      fetchLists,
      addToList,
      hasError,
    };
  },
});
</script>

<style lang="scss" scoped>
.list-form {
  .field-wrapper {
    margin-bottom: 1rem;
  }

  .field.field-error {
    :deep(.p-autocomplete),
    :deep(.p-inputtext),
    :deep(.p-dropdown),
    :deep(.edit-autocomplete) {
      .p-autocomplete-input,
      .p-inputtext {
        border-color: #ef4444 !important;
        
        &:focus {
          border-color: #dc2626 !important;
          box-shadow: 0 0 0 0.2rem rgba(239, 68, 68, 0.25) !important;
        }
      }
    }

    :deep(.button-group) {
      .p-button {
        &.p-button-outlined {
          border-color: #ef4444;
        }
      }
    }
  }
}
</style>
